
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import crossmap from "../../../assets/crossmap.svg";
import {
  GoogleMap,
  Marker,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";
import LocationPermissionModal from "../ClientAcquisition/InvitedClients/permissionModal";
import { MAPS_API_KEY } from "../../../components/mapKey";
import useGeolocationPermission from "../../../Utlis/useGeolocationPermission";

/* ---------- helpers ---------- */
const circularRadius = 100;
const containerStyle = {
  width: "100%",
  height: "100%",
};

const LocatingPill = ({ label, zIndex = 10 }) => (
  <Box
    sx={{
      position: "absolute",
      top: 12,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex,
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 0.75,
      backgroundColor: "#fff",
      borderRadius: 5,
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      fontSize: 13,
      fontFamily: "Poppins, sans-serif",
    }}
  >
    <CircularProgress size={16} />
    {label}
  </Box>
);

const Location = ({ onChange, initialLocation }) => {
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("No address found");
  const [showFullMap, setShowFullMap] = useState(false);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [locating, setLocating] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const addressRef = useRef(null);
  const locationRequestRef = useRef(0);
  const isOnline = () => navigator.onLine;

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY,
    libraries: ["places"],
  });

  /* ---- Init: API location (run ONCE only) ---- */
  useEffect(() => {
    if (initialLocation?.lat && initialLocation?.lng && !position && isLoaded) {
      const coords = {
        lat: parseFloat(initialLocation.lat),
        lng: parseFloat(initialLocation.lng),
      };
      setPosition(coords);
      fetchAddress(coords);
    }
  }, [initialLocation, position, isLoaded]);

  const updatePosition = (newPos) => {
    setPosition(newPos);
    fetchAddress(newPos);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("geolocationNotSupported") || "Geolocation not supported");
      return;
    }

    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    const requestId = ++locationRequestRef.current;
    const isStale = () => requestId !== locationRequestRef.current;

    setLocating(true);

    const onSuccess = (pos) => {
      if (isStale()) return;

      setShowPermissionModal(false);
      updatePosition({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      });
    };

    const onFinalError = (error) => {
      if (isStale()) return;

      setLocating(false);

      if (error?.code === error.PERMISSION_DENIED) {
        setShowPermissionModal(true);
        return;
      }

      setAddress(t("unableToFetchLocation"));
      toast.error(t("unableToFetchLocationToast"));
    };

    // Attempt 1: fast network/Wi-Fi fix, cached position up to 1 min old
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (error) => {
        if (isStale()) return;

        if (error.code === error.PERMISSION_DENIED) {
          onFinalError(error);
          return;
        }

        // Attempt 2: GPS, can be slow (especially on iOS)
        navigator.geolocation.getCurrentPosition(onSuccess, onFinalError, {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0,
        });
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  };

  /* ---- Fetch structured address using Google Geocoding ---- */
  const fetchAddress = ({ lat, lng }) => {
    if (!isLoaded || !window.google?.maps) {
      setLocating(false);
      return;
    }

    if (!isOnline()) {
      setLocating(false);
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      setLocating(false);

      if (status === "OK" && results[0]) {
        const formattedAddress = results[0].formatted_address;
        const addressComponents = results[0].address_components;

        console.log("🗺️ Reverse Geocoding Result:", results[0]);

        let country = "";
        let province = "";
        let city = "";
        let community = "";
        let postalCode = "";
        let streetAddress = "";
        let region = "";
        let countryCode = "";
        let continent = "";

        // Parse address components
        addressComponents.forEach((component) => {
          const types = component.types;

          if (types.includes("country")) {
            country = component.long_name;
            countryCode = component.short_name;
          }

          if (types.includes("administrative_area_level_1")) {
            province = component.long_name;
            region = component.long_name; // Use as region
          }

          if (types.includes("locality") || types.includes("postal_town")) {
            city = component.long_name;
          }

          if (types.includes("sublocality") || types.includes("neighborhood")) {
            community = component.long_name;
          }

          if (types.includes("administrative_area_level_2")) {
            if (!community) community = component.long_name;
          }

          if (types.includes("postal_code")) {
            postalCode = component.long_name;
          }

          if (types.includes("route")) {
            streetAddress = component.long_name;
          }

          if (types.includes("street_number")) {
            streetAddress = component.long_name + " " + streetAddress;
          }
        });

        // If city not found, try alternative
        if (!city) {
          addressComponents.forEach((component) => {
            if (component.types.includes("administrative_area_level_2")) {
              city = component.long_name;
            }
          });
        }

        // Set continent based on country (simplified)
        if (country) {
          continent = "North America"; // You can expand this logic
        }

        console.log("📍 Extracted Address Components:", {
          country,
          countryCode,
          province,
          city,
          community,
          postalCode,
          streetAddress,
          region,
          continent,
        });

        const structured = {
          latitude: lat,
          longitude: lng,
          address: formattedAddress,
          streetAddress,
          city,
          province,
          community,
          postalCode,
          country,
          countryCode,
          region,
          continent,
        };

        setAddress(formattedAddress);
        if (addressRef.current) {
          addressRef.current.value = formattedAddress;
        }

        onChange?.(structured);
      } else {
        console.error("❌ Geocoding failed:", status);
        setAddress("Address not found");

        onChange?.({
          latitude: lat,
          longitude: lng,
          address: "Address not found",
          streetAddress: "",
          city: "",
          province: "",
          community: "",
          postalCode: "",
          country: "",
          countryCode: "",
          region: "",
          continent: "",
        });
      }
    });
  };

  /* ---- Search with Autocomplete ---- */
  useEffect(() => {
    if (!searchQuery.trim() || !isLoaded) {
      setSuggestions([]);
      return;
    }

    if (!isOnline()) {
      setSuggestions([]);
      return;
    }

    const timeout = setTimeout(() => {
      const service = new window.google.maps.places.AutocompleteService();

      service.getPlacePredictions(
        { input: searchQuery },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchQuery, isLoaded]);

  /* ---- Handle Search Select ---- */
  const handleSearchSelect = (sug) => {
    if (!isLoaded) return;

    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId: sug.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;

        const newPos = {
          lat: location.lat(),
          lng: location.lng(),
        };

        console.log("🔍 Search Selected Location:", newPos);

        setPosition(newPos);
        fetchAddress(newPos);
        setSearchQuery(results[0].formatted_address);
        setSuggestions([]);
      }
    });
  };

  /* ---- Marker drag handler ---- */
  const handleMarkerDragEnd = (e) => {
    const newPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setPosition(newPos);
    fetchAddress(newPos);
  };

  // Reflect the browser's location setting changing while the page is open
  useGeolocationPermission({
    retryOnFocus: showPermissionModal,
    onGranted: () => {
      if (!locating && (showPermissionModal || !position)) getCurrentLocation();
    },
    onDenied: () => {
      setShowPermissionModal(true);
    },
  });

  // Safe fallback center
  const mapCenter = position || { lat: 37.0902, lng: -95.7129 };

  /* ---- Monitor internet connectivity ---- */
  useEffect(() => {
    const handleOnline = () => {
      toast.success(t("internetRestored") || "Internet connection restored");
      // Retry: refresh address for an existing pin, otherwise locate the user
      if (position?.lat && position?.lng) {
        fetchAddress(position);
      } else {
        getCurrentLocation();
      }
    };

    const handleOffline = () => {
      toast.error(t("internetConnectionLost") || "Internet connection lost");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [position]);

  if (loadError) return <Box>Error loading maps</Box>;
  if (!isLoaded) return <Box>Loading maps...</Box>;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <CustomText
          customStyle={{
            ...textStyles.body4,
            color: theme.palette.text.primary,
          }}
        >
          {t("pickFromLive")} <span style={{ color: "red" }}>*</span>
        </CustomText>

        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => setSearchBarOpen((prev) => !prev)}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: 1,
              width: 40,
              height: 31,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={getCurrentLocation}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              borderRadius: 1,
              width: 40,
              height: 31,
              "&:hover": { backgroundColor: "#1976d2" },
            }}
          >
            <RoomIcon fontSize="small" />
          </IconButton>
          <CustomButton
            label={t("fullMap")}
            size="small"
            sx={{
              height: 31,
              px: 2,
              backgroundColor: "#1976d2",
              color: "#fff",
              fontSize: 12,
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={() => setShowFullMap(true)}
          />
        </Box>
      </Box>

      {/* Search input with suggestions */}
      {searchBarOpen && (
        <Box sx={{ mb: 2, position: "relative" }}>
          <input
            type="text"
            placeholder={t("searchLocation")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            style={{
              width: "100%",
              padding: "10px 14px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              fontFamily: "Poppins, sans-serif",
            }}
          />

          {suggestions.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                zIndex: 1000,
                background: "#fff",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 2,
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              {suggestions.map((sug, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                  onClick={() => handleSearchSelect(sug)}
                >
                  {sug.description}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Map preview */}
      <Box
        sx={{
          position: "relative",
          height: 300,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {locating && <LocatingPill label={t("fetchingLocation")} />}
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={15}
        >
          {position && (
            <>
              <Marker
                position={position}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
              <Circle
                center={position}
                radius={circularRadius}
                options={{
                  fillColor: "#3f51b5",
                  fillOpacity: 0.2,
                  strokeColor: "#3f51b5",
                }}
              />
            </>
          )}
        </GoogleMap>
      </Box>

      {/* Address field */}
      <textarea
        ref={addressRef}
        readOnly
        value={address}
        rows={2}
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "16px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          marginTop: "10px",
          backgroundColor: "#f4f4f4",
          fontFamily: "Poppins, sans-serif",
          resize: "vertical",
          overflowY: "auto",
          lineHeight: "1.4",
        }}
      />

      {/* Full Map modal */}
      {showFullMap && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "#fff",
            zIndex: 1300,
          }}
        >
          <IconButton
            onClick={() => setShowFullMap(false)}
            sx={{ position: "absolute", top: 16, right: 16, zIndex: 1400 }}
          >
            <img src={crossmap} alt="close" style={{ width: 50, height: 50 }} />
          </IconButton>
          {locating && (
            <LocatingPill label={t("fetchingLocation")} zIndex={1400} />
          )}
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={15}
          >
            {position && (
              <>
                <Marker
                  position={position}
                  draggable
                  onDragEnd={handleMarkerDragEnd}
                />
                <Circle
                  center={position}
                  radius={circularRadius}
                  options={{
                    fillColor: "#3f51b5",
                    fillOpacity: 0.2,
                    strokeColor: "#3f51b5",
                  }}
                />
              </>
            )}
          </GoogleMap>
        </Box>
      )}

      {/* Permission modal */}
      <LocationPermissionModal
        open={showPermissionModal}
        onAllow={() => {
          setShowPermissionModal(false);
          getCurrentLocation();
        }}
        onCancel={() => setShowPermissionModal(false)}
      />
    </Box>
  );
};

export default Location;